import { useQuery } from "@tanstack/react-query";
import {
  MapPin,
  Calendar,
  User,
  FileText,
  Shield,
  Microscope,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import type { AssaultIncidentRecord } from "./AssaultIncidentForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AssaultIncidentDetailProps {
  personId: string;
  incidentId: number;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AssaultIncidentDetail({
  personId,
  incidentId,
  onBack,
}: AssaultIncidentDetailProps) {
  const { data, isLoading } = useQuery<{ data: AssaultIncidentRecord }>({
    queryKey: ["assaultIncident", personId, incidentId],
    queryFn: () =>
      api.get<{ data: AssaultIncidentRecord }>(
        `/clients/${personId}/assault-incidents/${incidentId}`,
      ),
    enabled: !!personId && !!incidentId,
  });

  const incident = data?.data;

  function renderJsonOrText(raw: string | null, emptyMsg: string) {
    if (!raw) {
      return (
        <p className="text-sm text-muted-foreground italic">{emptyMsg}</p>
      );
    }
    try {
      const parsed = JSON.parse(raw);
      return (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-muted-foreground capitalize">
                {key.replace(/_/g, " ")}
              </dt>
              <dd className="font-medium">{String(value)}</dd>
            </div>
          ))}
        </dl>
      );
    } catch {
      return (
        <p className="text-sm text-foreground whitespace-pre-wrap">{raw}</p>
      );
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={onBack}>
          Back to List
        </Button>
        <div className="h-40 animate-pulse rounded-lg border bg-muted" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={onBack}>
          Back to List
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Incident not found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const perpetratorName =
    incident.perpetrator_first_name && incident.perpetrator_last_name
      ? `${incident.perpetrator_first_name} ${incident.perpetrator_last_name}`
      : null;

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={onBack}>
        Back to List
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            Assault Incident #{incident.id}
          </CardTitle>
          <CardDescription>
            Created {new Date(incident.created_at).toLocaleDateString()}
            {incident.author_first_name &&
              ` by ${incident.author_first_name} ${incident.author_last_name}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary info */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Date:</span>
              <span className="font-medium">
                {incident.incident_date
                  ? new Date(incident.incident_date).toLocaleDateString()
                  : "Not specified"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Location:</span>
              <span className="font-medium">
                {incident.location ?? "Not specified"}
              </span>
            </div>
            {perpetratorName && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Perpetrator:</span>
                <span className="font-medium">
                  {perpetratorName}
                  {incident.perpetrator_relationship &&
                    ` (${incident.perpetrator_relationship})`}
                </span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" />
              Incident Details
            </h4>
            {renderJsonOrText(incident.details, "No details recorded.")}
          </div>

          {/* SART Response */}
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <Shield className="h-4 w-4" />
              SART Response
            </h4>
            {renderJsonOrText(
              incident.sart_response,
              "No SART response information recorded.",
            )}
          </div>

          {/* Forensic Exam */}
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <Microscope className="h-4 w-4" />
              Forensic Exam
            </h4>
            {renderJsonOrText(
              incident.forensic_exam,
              "No forensic exam information recorded.",
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
